/*!
 * Copyright MaxDesign. All Rights Reserved. 
  ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
  ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 */

customElements.define(
  'tabs-maxdesign',
  class sectionTabs extends HTMLElement {
    constructor() {
      super();
      this.settings = JSON.parse(this.querySelector(':scope > [type="application/json"]').innerText);
      this.setVariables();
      this.allocateSections();
      this.scrollbarWidth();
      this.tabToggles();
      this.tabDecoration();
      if (this.allowSticky) {
        this.settings.accordion && this.shadowTabsObserver();
        this.tabsAutoScrollObserver();
      }
      this.stickyOffset = this.headerHeight;
      this.headerMutationObserver();
      this.settings.accordion ? this.mobileAccordion() : this.swipeTabs();
      addEventListener('resize', this.onResize.bind(this));
    }

    setVariables() {
      this.DOM = {
        bar: this.querySelector('.md-tabs__bar'),
        list: this.querySelector('.md-tabs__list'),
        items: this.querySelectorAll('.md-tabs__list li'),
        content: this.querySelectorAll('[data-tab-content]'),
        topSentinel: this.querySelector('.md-tabs__sentinel--top'),
        bottomSentinel: this.querySelector('.md-tabs__sentinel--bottom'),
        placeholders: this.querySelectorAll('tab-placeholder'),
        accordion: {
          items: this.querySelectorAll('.md-tabs__container .md-tabs__item'),
          sentinels: this.querySelectorAll('.md-tabs__sentinel--tab'),
        },
        headerFixedEls: this.settings.headerSelector ? document.querySelectorAll(this.settings.headerSelector) : null,
      };
    }

    /* #region  Main methods */

    /**
     * Update some methods on resize
     */
    onResize() {
      this.scrollbarWidth();
      this.tabDecoration();
      this.stickyOffset = this.headerHeight;
    }

    /**
     * Toggle active tabs
     * Store tabs length in array
     * We update section data-tab-active attr to toggle tab content
     * Recenter scroll position if clicked tab overflows viewport
     */
    tabToggles() {
      for (let [index, tab] of this.DOM.items.entries()) {
        tab.addEventListener('click', e => {
          const boundingTab = tab.getBoundingClientRect();
          for (let item of this.DOM.items) item.toggleAttribute('data-open', item == tab);
          this.parentElement.dataset.tabActive = tab.dataset.tabItem;
          this.barTransition(index);
          if (boundingTab.left < 0 || boundingTab.right > innerWidth) {
            this.DOM.list.scrollTo({
              left: e.target.closest('li').offsetLeft - 25,
              behavior: 'smooth',
            });
          }
        });
      }
    }

    /**
     * Allocate sections to matching tabs
     */
    allocateSections() {
      const tabEls = document.querySelectorAll('[data-tab-allocate]:not([data-tab-allocate=""])');
      for (let tabEl of tabEls) {
        const section = tabEl.parentElement;
        const tab = [...this.DOM.content].find(el => el.dataset.tabContent == tabEl.dataset.tabAllocate);
        if (!tab) continue;
        if (Shopify.designMode) {
          section.classList.remove('shopify-section');
          section.removeAttribute('data-shopify-editor-section');
        }
        switch (tabEl.dataset.tabPosition) {
          case 'bottom':
            tab.querySelector('.md-tabs__content').lastElementChild.after(section);
            break;
          case 'top':
            tab.querySelector('.md-tabs__content > div:not([class|="shopify-section"])').before(section);
            break;
          default:
            for (let placeholder of this.DOM.placeholders)
              if (placeholder.getAttribute('section') == tabEl.dataset.tabPosition) placeholder.replaceWith(section);
        }
      }
    }

    /**
     * Scroll to sticky tabs based on sentinel position
     */
    tabsAutoScrollObserver() {
      this.scrollListenerHandler = () => this.smoothScroll(window, this.yAxisValue, 250);
      const observer = new IntersectionObserver(([entry]) => (this.clickScrollReset = !entry.isIntersecting));
      observer.observe(this.DOM.topSentinel);
    }

    /**
     * Check if sticky header change state
     * setTimeout in case sticky header animation has transition
     */
    headerMutationObserver() {
      const observer = new MutationObserver(() => setTimeout(() => (this.stickyOffset = this.headerHeight), 500));
      document.querySelectorAll(this.settings.headerSelector).forEach(el => observer.observe(el, { attributes: true }));
    }

    swipeTabs() {
      for (let tab of this.DOM.content) {
        tab.addEventListener('touchstart', this.handleTouchStart.bind(this));
        tab.addEventListener('touchend', this.handleTouchEnd.bind(this));
      }
    }
    /* #endregion */

    /* #region Utility methods */

    handleTouchStart(e) {
      this.startX = e.changedTouches[0].screenX;
      this.startY = e.changedTouches[0].screenY;
    }

    handleTouchEnd(e) {
      const diffX = e.changedTouches[0].screenX - this.startX;
      const diffY = e.changedTouches[0].screenY - this.startY;
      const ratioX = Math.abs(diffX / diffY);
      const ratioY = Math.abs(diffY / diffX);
      const absDiff = Math.abs(ratioX > ratioY ? diffX : diffY);
      // Ignore small movements & vertical swipes
      if (absDiff < 50 || ratioY * 2 > ratioX) return;
      this.swapTab(diffX >= 0 ? 'prev' : 'next');
    }

    swapTab(direction) {
      const activeTab = this.querySelector('.md-tabs__list .md-tabs__item[data-open]');
      const newTab = direction == 'prev' ? activeTab.previousElementSibling : activeTab.nextElementSibling;
      if (newTab) newTab.click();
    }

    /**
     * Smooth scroll polyfill by maxdesign
     * @param {HTMLObjectElement} node - window or DOM node
     * @param {Number} target - X or Y value
     * @param {Number} duration - default to 400
     * @param {Boolean} horizontal - default to false (vertical)
     * @returns scroll method used
     */
    smoothScroll(node, target, duration = 400, horizontal = false) {
      if ('scrollBehavior' in document.documentElement.style) {
        return node.scrollTo({
          [horizontal ? 'left' : 'top']: target,
          behavior: 'smooth',
        });
      } else {
        const startTime = Date.now();
        const scrollDirection = horizontal ? 'scrollLeft' : 'scrollTop';
        const offset = node[scrollDirection];
        const gap = target - offset;
        const easingOutQuint = t => --t * t * t + 1;
        const step = () => {
          const elapsed = Date.now() - startTime;
          const percentage = elapsed / duration;
          if (percentage >= 1) return horizontal ? node.scrollBy(0, 1) : node.scrollBy(1, 0);
          node[scrollDirection] = Math.round(gap * easingOutQuint(elapsed / duration) + offset);
          requestAnimationFrame(step);
        };
        step();
      }
    }

    /**
     * Tab bar transiiton
     * @param {Number} index - index of tab
     */
    barTransition(index) {
      this.style.setProperty('--tab-width', `${this.tabsWidth[index][0]}px`);
      this.style.setProperty('--tab-transform', `translateX(${this.tabsWidth[index][1]}px)`);
    }

    /**
     * Observe accordion tab interaction and toggle sticky attribute
     */
    shadowTabsObserver() {
      const observer = new IntersectionObserver(entries =>
        entries.forEach(entry =>
          entry.target.parentElement.firstElementChild.toggleAttribute('data-sticky', !entry.isIntersecting)
        )
      );
      this.DOM.accordion.sentinels.forEach(sentinel => observer.observe(sentinel));
    }

    /**
     * Ignore lateral margins
     * Not using window.innerWidth because it seems unreliable at times
     */
    scrollbarWidth() {
      const el = Object.assign(document.createElement('div'), { style: 'overflow:scroll;visibility:hidden' });
      document.body.appendChild(el);
      document.documentElement.style.setProperty('--sbw', el.offsetWidth - el.clientWidth + 'px');
      el.remove();
    }

    /**
     * Keep a record of every tab width for pseudo element decoration
     */
    tabDecoration() {
      this.tabsWidth = [];
      for (let [index, tab] of this.DOM.items.entries()) {
        setTimeout(
          () => {
            this.tabsWidth.push([
              tab.offsetWidth,
              this.DOM.list.scrollLeft + tab.getBoundingClientRect().left - this.DOM.list.getBoundingClientRect().left,
            ]);
            tab.hasAttribute('data-open') && this.barTransition(index);
          },
          Shopify.designMode ? 100 : 0
        );
      }
    }

    /**
     * get total height of sticky elements
     * we offset 1px for iOS glitch
     * @param {Object} tab - make calculation based on currently open tab
     * @return header component(s) height
     */
    get headerHeight() {
      return [...this.DOM.headerFixedEls].reduce((totalHeight, el) => {
        const elBottom = Math.floor(el.getBoundingClientRect().bottom);
        const isVisible = elBottom >= -1;
        return isVisible ? (totalHeight += el.offsetHeight) : totalHeight;
      }, 0);
    }

    /**
     * whether or not medium should be sticky
     */
    get allowSticky() {
      return (this.isMobile && this.settings.stickyMobile) || (!this.isMobile && this.settings.stickyDesktop);
    }

    /**
     * Y scroll axis + viewport distance from top
     */
    get yAxisValue() {
      return this.getBoundingClientRect().top + document.documentElement.scrollTop - this.headerHeight;
    }

    /**
     * Check if mobile version
     * @returns Boolean
     */
    get isMobile() {
      return !matchMedia(`(min-width:${this.settings.breakpoint}px)`).matches;
    }

    /**
     * Set sticky tab offset
     */
    set stickyOffset(headerHeight) {
      this.style.setProperty('--tab-top-offset', headerHeight + 'px');
    }

    set clickScrollReset(state) {
      this.DOM.list[state ? 'addEventListener' : 'removeEventListener']('click', this.scrollListenerHandler);
    }

    /* #endregion */

    /* #region Mobile Accordion */

    /**
     * Vertical tabs in mobile
     */
    mobileAccordion() {
      for (let tab of this.DOM.accordion.items) {
        tab.addEventListener('click', () => {
          const prevTabRectTop = this.prevTabRectTop; // getBoundingClientRect top of current open tab
          this.DOM.accordion.items.forEach(el =>
            el.toggleAttribute('data-open', el == tab && !tab.hasAttribute('data-open'))
          );
          // get new tab height & header offset height
          const tabHeight = this.tabHeight(tab);
          // animate tab differently based on current tab scroll position
          if (this.prevOpenTab && prevTabRectTop - this.headerHeight < 0) {
            this.prevOpenTabContent.style.transition = this.tabContent.style.transition = 'none';
            this.tabContent.style.height = 'auto';
            this.tabContent.style.height = tabHeight + 'px';
            scrollTo({
              top: scrollY + prevTabRectTop - (parseInt(this.style.getPropertyValue('--tab-top-offset'), 10) || 0),
              behavior: 'auto',
            });
          } else {
            this.tabContent.style.transition = 'height .4s ease';
            setTimeout(() => (this.tabContent.style.height = tabHeight + 'px'));
          }
          this.uncompleteImgLoad();
        });
      }
    }

    /**
     * @returns offset to scroll based on previous tab
     */
    get prevTabRectTop() {
      this.prevOpenTab = this.querySelector('.md-tabs__container .md-tabs__item[data-open]');
      this.prevOpenTabContent = this.prevOpenTab && this.prevOpenTab.nextElementSibling;
      return this.prevOpenTab ? this.prevOpenTabContent.getBoundingClientRect().top - this.prevOpenTab.offsetHeight : 0;
    }

    /**
     * get tab height for height animaiton
     * @param {Object} tab - clicked tab
     */
    get tabHeight() {
      return tab => {
        this.tabContent = tab.nextElementSibling;
        this.tabContent.style.height = 'auto';
        const tabHeight = this.tabContent.offsetHeight;
        this.tabContent.style.height = '0px';
        return tabHeight;
      };
    }

    /**
     * Default tab content height to auto if an image is not laoded yet
     */
    uncompleteImgLoad() {
      if (this.tabContent.querySelector('img')) {
        if (!this.tabContent.querySelector('img').complete) this.tabContent.style.height = 'auto';
      }
    }
    /* #endregion */
  }
);
